import { Component } from '@theme/component';

/**
 * <plw-story-form> — the "Share your story" mini-app (CQ-2042).
 *
 * Wraps the whole page (titles + form + thank-you card) so refs cover both
 * states. Visitors either type their story or hit "Record audio": the mic
 * stream is captured by MediaRecorder while SpeechRecognition (where the
 * browser has it — not Firefox) live-transcribes into the textarea. Submit
 * POSTs the text straight to the Airtable REST API (CORS is open) and, if a
 * recording exists, base64-uploads the audio to the record's attachment field
 * (content.airtable.com, ≤5MB). No backend by design: the token is a PAT
 * scoped to data.records:write ONLY on a dedicated Stories base, so a leaked
 * token can create spam records but can't read or enumerate anything.
 *
 * States on data-state: idle → requesting → recording → recorded →
 * submitting → done | error. Visibility is CSS-driven off the attribute
 * (see sections/plw-share-story.liquid); all copy is server-rendered
 * (translated) in data-copy-* attributes — no strings live here.
 *
 * SpeechRecognition never exposes the audio, hence the parallel recorder.
 * Chrome quirks handled: recognition auto-stops on silence (restart in
 * onend while recording) and webm blobs report duration = Infinity (the
 * elapsed-time counter is the duration source of truth).
 *
 * Limits (section settings, on data-max-words / data-max-seconds): the box
 * shows a live word counter and, while recording, a countdown timer.
 * Recording auto-stops at the time limit or when the transcript hits the
 * word cap; typing past the cap is allowed but submit is blocked until the
 * story is back under it.
 */

/**
 * @typedef {object} StoryFormRefs
 * @property {HTMLHeadingElement} promptTitle - The prompt <h1>
 * @property {HTMLHeadingElement} thanksTitle - The thank-you <h1> (focus target)
 * @property {HTMLTextAreaElement} story - The story textarea
 * @property {HTMLButtonElement} recordButton - Record / stop / re-record button
 * @property {HTMLElement} recordLabel - Text inside the record button
 * @property {HTMLElement} player - The audio player row
 * @property {HTMLButtonElement} playButton - Play / pause toggle
 * @property {SVGElement} playIcon - Play triangle
 * @property {SVGElement} pauseIcon - Pause bars
 * @property {HTMLInputElement} seek - Seek slider
 * @property {HTMLElement} time - "0:00 / 1:23" display
 * @property {HTMLElement} [timer] - "0:12 / 1:00" countdown shown while recording
 * @property {HTMLElement} [wordCount] - "12 / 200 words" counter
 * @property {HTMLAudioElement} audio - Hidden playback engine
 * @property {HTMLInputElement} consent - Consent checkbox
 * @property {HTMLButtonElement} submitButton - "Share story"
 * @property {HTMLElement} status - aria-live status region
 * @property {HTMLElement} card - The white share card
 * @property {HTMLElement} cardStory - Story text inside the card
 * @property {HTMLElement} [cardDomain] - Brand domain signing the card (optional)
 */

/** @extends {Component<StoryFormRefs>} */
class PlwStoryForm extends Component {
  requiredRefs = ['story', 'recordButton', 'recordLabel', 'status'];

  /** @type {MediaStream | null} */
  #stream = null;
  /** @type {MediaRecorder | null} */
  #recorder = null;
  /** @type {SpeechRecognition | null} */
  #recognition = null;
  /** @type {Blob[]} */
  #chunks = [];
  /** @type {Blob | null} */
  #blob = null;
  /** @type {string | null} */
  #audioUrl = null;
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {ReturnType<typeof setInterval> | null} */
  #ticker = null;
  #recording = false;
  #elapsedSeconds = 0;
  /** Transcript text committed before the current recording session. */
  #committed = '';
  /** Finalized speech results of the current recording session. */
  #finals = '';
  /** Status shown when a limit auto-stops the recording (read in #finishRecording). */
  #limitStopMessage = '';

  connectedCallback() {
    super.connectedCallback();
    // Media events aren't in the framework's delegated-event list — wire the
    // playback engine directly.
    const { audio } = this.refs;
    audio?.addEventListener('play', this.#onAudioPlay);
    audio?.addEventListener('pause', this.#onAudioPause);
    audio?.addEventListener('ended', this.#onAudioEnded);
    audio?.addEventListener('timeupdate', this.#onTimeUpdate);
    // bfcache / form-restore can bring back a typed story + checked consent.
    this.#syncHasContent();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const { audio } = this.refs;
    audio?.removeEventListener('play', this.#onAudioPlay);
    audio?.removeEventListener('pause', this.#onAudioPause);
    audio?.removeEventListener('ended', this.#onAudioEnded);
    audio?.removeEventListener('timeupdate', this.#onTimeUpdate);
    this.#abortController?.abort();
    this.#teardownRecording();
    if (this.#audioUrl) URL.revokeObjectURL(this.#audioUrl);
  }

  get #state() {
    return this.dataset.state || 'idle';
  }

  /** @param {string} value */
  set #state(value) {
    this.dataset.state = value;
  }

  /** @param {string} key */
  #copy(key) {
    return this.dataset[key] || '';
  }

  #statusClearId = 0;

  /**
   * @param {string} text
   * @param {{ transient?: boolean }} [options] - transient: announce to the
   *   live region but wipe the visible text shortly after (CQ-2101 — status
   *   like "Recording stopped" shouldn't linger on screen; screen readers
   *   have already spoken it by then).
   */
  #announce(text, { transient = false } = {}) {
    clearTimeout(this.#statusClearId);
    this.refs.status.textContent = text;
    if (transient && text) {
      this.#statusClearId = setTimeout(() => {
        this.refs.status.textContent = '';
      }, 1500);
    }
  }

  #syncHasContent() {
    this.toggleAttribute('data-has-content', this.refs.story.value.trim().length > 0);
    this.#syncSubmit();
  }

  /**
   * The submit button is always visible but only enabled with a story AND
   * consent checked (client call 2026-08-21), and only when Airtable is wired.
   * Stays disabled while a submit is in flight.
   */
  #syncSubmit() {
    const { submitButton, story, consent } = this.refs;
    if (!submitButton) return;
    const { airtableToken, airtableBase, airtableTable } = this.dataset;
    const configured = Boolean(airtableToken && airtableBase && airtableTable);
    const ready = configured && story.value.trim().length > 0 && Boolean(consent?.checked);
    submitButton.disabled = !ready || this.#state === 'submitting';
  }

  handleConsentChange() {
    this.#syncSubmit();
  }

  get #maxWords() {
    return Number(this.dataset.maxWords) || 0;
  }

  #syncWordCount() {
    const max = this.#maxWords;
    if (!max || !this.refs.wordCount) return;
    const words = countWords(this.refs.story.value);
    const template = this.#copy('copyWordCount') || '{current} / {max}';
    this.refs.wordCount.textContent = template
      .replace('{current}', String(words))
      .replace('{max}', String(max));

    const wasOver = this.hasAttribute('data-over-words');
    const over = words > max;
    this.toggleAttribute('data-over-words', over);
    // Announce once on crossing the cap, not on every keystroke past it.
    if (over && !wasOver) this.#announce(this.#copy('copyWordLimit'), { transient: true });
  }

  handleStoryInput() {
    this.#syncHasContent();
    this.#syncWordCount();
  }

  // --- Recording -----------------------------------------------------------

  async toggleRecord() {
    if (this.#recording) {
      this.#stopRecording();
      return;
    }

    if (!navigator.mediaDevices?.getUserMedia) {
      this.#announce(this.#copy('copyMicUnavailable'));
      return;
    }

    this.#state = 'requesting';
    this.#announce(this.#copy('copyRequesting'));

    try {
      this.#stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (error) {
      console.error(error);
      this.#state = this.#blob ? 'recorded' : 'idle';
      this.#announce(this.#copy('copyMicUnavailable'));
      return;
    }

    // Re-record replaces the previous audio; the transcript text stays (new
    // speech appends to it).
    this.#discardAudio();
    this.#startRecorder();
    this.#startRecognition();
    this.#startTicker();
    this.#recording = true;
    this.#state = 'recording';
    this.refs.recordLabel.textContent = this.#copy('copyStop');
    this.#announce(this.#copy('copyRecordingStarted'));
  }

  #startRecorder() {
    if (!this.#stream) return;
    const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
      ? 'audio/webm;codecs=opus'
      : 'audio/mp4';
    // 64kbps mono speech ≈ 480KB/min — the recording cap (data-max-seconds,
    // a section setting capped at 10 min) stays inside Airtable's 5MB
    // base64-upload limit.
    this.#recorder = new MediaRecorder(this.#stream, { mimeType, audioBitsPerSecond: 64000 });
    this.#chunks = [];
    this.#recorder.addEventListener('dataavailable', (event) => {
      if (event.data.size > 0) this.#chunks.push(event.data);
    });
    this.#recorder.addEventListener('stop', () => this.#finishRecording());
    this.#recorder.start(1000);
  }

  #startRecognition() {
    const Recognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Recognition) return; // Firefox: audio still records, typing is the transcript.

    this.#committed = this.refs.story.value.trim();
    if (this.#committed) this.#committed += '\n\n';
    this.#finals = '';

    const recognition = new Recognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = document.documentElement.lang || 'en';

    recognition.addEventListener('result', (event) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i];
        if (result.isFinal) {
          this.#finals += result[0].transcript;
        } else {
          interim += result[0].transcript;
        }
      }
      const story = this.refs.story;
      story.value = this.#committed + this.#finals + interim;
      story.scrollTop = story.scrollHeight;
      this.#syncHasContent();
      // Word cap: trim the overflow and stop — #finishRecording announces it.
      const max = this.#maxWords;
      if (max && this.#recording && countWords(story.value) > max) {
        story.value = truncateWords(story.value, max);
        this.#limitStopMessage = this.#copy('copyWordLimit');
        this.#stopRecording();
      }
      this.#syncWordCount();
    });
    // Chrome ends recognition on silence — restart while still recording.
    recognition.addEventListener('end', () => {
      if (!this.#recording || this.#recognition !== recognition) return;
      try {
        recognition.start();
      } catch {
        // Already restarting — ignore.
      }
    });
    // On any recognition failure keep recording audio-only.
    recognition.addEventListener('error', (event) => console.error('SpeechRecognition:', event.error));

    this.#recognition = recognition;
    recognition.start();
  }

  #startTicker() {
    this.#elapsedSeconds = 0;
    const maxSeconds = Number(this.dataset.maxSeconds) || 60;
    this.#updateTimer(maxSeconds);
    this.#ticker = setInterval(() => {
      this.#elapsedSeconds += 1;
      this.#updateTimer(maxSeconds);
      if (this.#elapsedSeconds >= maxSeconds) {
        this.#limitStopMessage = this.#copy('copyTimeLimit');
        this.#stopRecording();
      }
    }, 1000);
  }

  /** @param {number} maxSeconds */
  #updateTimer(maxSeconds) {
    if (!this.refs.timer) return;
    this.refs.timer.textContent = `${formatTime(this.#elapsedSeconds)} / ${formatTime(maxSeconds)}`;
  }

  #stopRecording() {
    if (!this.#recording) return;
    this.#recording = false;
    if (this.#ticker) clearInterval(this.#ticker);
    this.#ticker = null;
    this.#recognition?.stop();
    this.#recognition = null;
    // The recorder's `stop` event flushes the last chunk, then #finishRecording runs.
    if (this.#recorder && this.#recorder.state !== 'inactive') this.#recorder.stop();
  }

  #finishRecording() {
    const mimeType = this.#recorder?.mimeType || 'audio/webm';
    this.#releaseStream();
    this.#recorder = null;

    this.#blob = new Blob(this.#chunks, { type: mimeType });
    this.#chunks = [];
    this.#audioUrl = URL.createObjectURL(this.#blob);
    if (this.refs.audio) this.refs.audio.src = this.#audioUrl;
    if (this.refs.seek) {
      this.refs.seek.max = String(this.#elapsedSeconds || 1);
      this.refs.seek.value = '0';
    }
    this.#updateTime(0);

    this.toggleAttribute('data-has-audio', true);
    this.#syncHasContent();
    this.#state = 'recorded';
    this.refs.recordLabel.textContent = this.#copy('copyRecordAgain');
    this.#announce(this.#limitStopMessage || this.#copy('copyRecordingStopped'), { transient: true });
    this.#limitStopMessage = '';
    this.refs.story.focus();
  }

  #discardAudio() {
    this.refs.audio?.pause();
    if (this.#audioUrl) URL.revokeObjectURL(this.#audioUrl);
    this.#audioUrl = null;
    this.#blob = null;
    this.#chunks = [];
    this.toggleAttribute('data-has-audio', false);
  }

  #releaseStream() {
    if (!this.#stream) return;
    for (const track of this.#stream.getTracks()) track.stop();
    this.#stream = null;
  }

  #teardownRecording() {
    this.#recording = false;
    if (this.#ticker) clearInterval(this.#ticker);
    this.#ticker = null;
    this.#recognition?.stop();
    this.#recognition = null;
    if (this.#recorder && this.#recorder.state !== 'inactive') this.#recorder.stop();
    this.#recorder = null;
    this.#releaseStream();
  }

  // --- Playback ------------------------------------------------------------

  /** Recorded duration in seconds — Chrome webm blobs report Infinity, so the
   *  elapsed counter is the source of truth. */
  get #duration() {
    const native = this.refs.audio?.duration;
    if (native && Number.isFinite(native)) return native;
    return this.#elapsedSeconds;
  }

  togglePlay() {
    const { audio } = this.refs;
    if (!audio) return;
    if (audio.paused) {
      audio.play();
    } else {
      audio.pause();
    }
  }

  #onAudioPlay = () => this.#setPlayingUi(true);

  #onAudioPause = () => this.#setPlayingUi(false);

  #onAudioEnded = () => {
    this.#setPlayingUi(false);
    if (this.refs.seek) this.refs.seek.value = '0';
    this.#updateTime(0);
  };

  #onTimeUpdate = () => {
    const current = this.refs.audio?.currentTime || 0;
    if (this.refs.seek) this.refs.seek.value = String(current);
    this.#updateTime(current);
  };

  handleSeek() {
    const { audio, seek } = this.refs;
    if (!audio || !seek) return;
    audio.currentTime = Number(seek.value);
    this.#updateTime(audio.currentTime);
  }

  /** @param {boolean} playing */
  #setPlayingUi(playing) {
    const { playButton, playIcon, pauseIcon } = this.refs;
    if (!playButton) return;
    playButton.setAttribute('aria-label', playing ? this.#copy('copyPause') : this.#copy('copyPlay'));
    if (playIcon) playIcon.toggleAttribute('hidden', playing);
    if (pauseIcon) pauseIcon.toggleAttribute('hidden', !playing);
  }

  /** @param {number} current */
  #updateTime(current) {
    const total = this.#duration;
    if (this.refs.time) {
      this.refs.time.textContent = `${formatTime(current)} / ${formatTime(total)}`;
    }
    if (this.refs.seek) {
      const template = this.#copy('copyTimeOf') || '{current} of {total}';
      this.refs.seek.setAttribute(
        'aria-valuetext',
        template.replace('{current}', formatTime(current)).replace('{total}', formatTime(total))
      );
    }
  }

  // --- Submission ----------------------------------------------------------

  /** @param {SubmitEvent} event */
  async handleSubmit(event) {
    event.preventDefault();
    if (this.#state === 'submitting') return;
    if (this.#recording) this.#stopRecording();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    // Honeypot: bots that fill the hidden field get a quiet fake success.
    const honeypot = form.querySelector('[name="website"]');
    if (honeypot instanceof HTMLInputElement && honeypot.value) {
      this.#showThanks();
      return;
    }

    // Over the word cap (typing past it is allowed) — block until shortened.
    if (this.#maxWords && countWords(this.refs.story.value) > this.#maxWords) {
      this.#announce(this.#copy('copyWordLimit'));
      this.refs.story.focus();
      return;
    }

    const { airtableToken: token, airtableBase: base, airtableTable: table } = this.dataset;
    if (!token || !base || !table) {
      this.#state = 'error';
      this.#announce(this.#copy('copyError'));
      return;
    }

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    this.#state = 'submitting';
    this.#syncSubmit();
    this.#announce(this.#copy('copySubmitting'));

    try {
      const response = await fetch(
        `https://api.airtable.com/v0/${encodeURIComponent(base)}/${encodeURIComponent(table)}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            fields: {
              Prompt: this.dataset.prompt || '',
              Story: this.refs.story.value.trim(),
              Consent: true,
            },
            typecast: true,
          }),
          signal: this.#abortController.signal,
        }
      );
      if (!response.ok) throw new Error(`Airtable record create failed (${response.status})`);
      const record = await response.json();

      // The audio upload is best-effort — the story text is the record of
      // value, so a failed or oversized upload must not fail the submission.
      await this.#uploadAudio(record.id, token, base);

      this.#showThanks();
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.error(error);
      this.#state = 'error';
      this.#announce(this.#copy('copyError'));
    } finally {
      this.#syncSubmit();
    }
  }

  /**
   * @param {string} recordId
   * @param {string} token
   * @param {string} base
   */
  async #uploadAudio(recordId, token, base) {
    const field = this.dataset.airtableAudioField;
    const maxBytes = Number(this.dataset.maxBytes) || 4900000;
    if (!this.#blob || !field || this.#blob.size > maxBytes) return;

    try {
      const extension = this.#blob.type.includes('mp4') ? 'm4a' : 'webm';
      // Airtable 403s on a contentType with codec parameters
      // ("audio/webm;codecs=opus", Chrome's blob type) — send the bare type.
      const contentType = this.#blob.type.split(';')[0] || 'audio/webm';
      const response = await fetch(
        `https://content.airtable.com/v0/${encodeURIComponent(base)}/${recordId}/${encodeURIComponent(field)}/uploadAttachment`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            contentType,
            filename: `story.${extension}`,
            file: await toBase64(this.#blob),
          }),
          signal: this.#abortController?.signal,
        }
      );
      if (!response.ok) throw new Error(`Airtable audio upload failed (${response.status})`);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.error(error);
    }
  }

  #showThanks() {
    if (this.refs.cardStory) this.refs.cardStory.textContent = this.refs.story.value.trim();
    this.refs.promptTitle?.toggleAttribute('hidden', true);
    this.refs.thanksTitle?.toggleAttribute('hidden', false);
    this.#state = 'done';
    this.#announce('');
    this.refs.thanksTitle?.focus();
  }

  // --- Share-card image ----------------------------------------------------

  async handleDownload() {
    const prompt = this.dataset.prompt || '';
    const story = this.refs.cardStory?.textContent || '';
    // Signs the card, matching the on-page preview (Figma 3618:23541).
    const domain = this.refs.cardDomain?.textContent?.trim() || '';

    // 4:5 social canvas; brand faces are already on the page via
    // brand-fonts.liquid. Signifier 200 is the serif-24 scale weight.
    const width = 1080;
    const height = 1350;
    const margin = 96;
    const captionSize = 36;
    // Room kept clear at the foot of the canvas for the domain line.
    const footerHeight = domain ? captionSize + 48 : 0;
    await Promise.all([
      document.fonts.load('400 36px Calibre'),
      document.fonts.load('200 88px Signifier'),
      document.fonts.ready,
    ]);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) return;

    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, width, height);
    context.fillStyle = '#000000';

    context.font = `400 ${captionSize}px Calibre, sans-serif`;
    context.textBaseline = 'top';
    context.fillText(prompt, margin, margin, width - margin * 2);

    // Wrap the story, stepping the size down until it fits; ellipsize as the
    // last resort.
    const maxWidth = width - margin * 2;
    const maxTextHeight = height - margin * 2 - 120 - footerHeight;
    let lines = [];
    let fontSize = 88;
    for (const size of [88, 72, 56, 44]) {
      fontSize = size;
      context.font = `200 ${size}px Signifier, serif`;
      lines = wrapText(context, story, maxWidth);
      if (lines.length * size * 1.25 <= maxTextHeight) break;
    }
    const lineHeight = fontSize * 1.25;
    const maxLines = Math.floor(maxTextHeight / lineHeight);
    if (lines.length > maxLines) {
      lines = lines.slice(0, maxLines);
      lines[maxLines - 1] = `${lines[maxLines - 1].replace(/\s+\S*$/, '')}…`;
    }

    const blockHeight = lines.length * lineHeight;
    // Centred in what's left between the prompt and the domain line.
    let y = Math.max(margin + 120, (height - footerHeight - blockHeight) / 2);
    for (const line of lines) {
      context.fillText(line, margin, y);
      y += lineHeight;
    }

    if (domain) {
      context.font = `400 ${captionSize}px Calibre, sans-serif`;
      context.fillStyle = 'rgb(0 0 0 / 0.3)';
      context.fillText(domain, margin, height - margin - captionSize, maxWidth);
    }

    canvas.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'my-story.png';
      link.click();
      URL.revokeObjectURL(url);
    }, 'image/png');
  }
}

/** @param {string} text */
const countWords = (text) => {
  const trimmed = text.trim();
  return trimmed ? trimmed.split(/\s+/).length : 0;
};

/**
 * Cuts `text` after its `max`-th word, preserving the whitespace (paragraph
 * breaks) before the cut.
 * @param {string} text
 * @param {number} max
 */
const truncateWords = (text, max) => {
  let count = 0;
  const words = /\S+/g;
  let match;
  while ((match = words.exec(text))) {
    count += 1;
    if (count === max) return text.slice(0, match.index + match[0].length);
  }
  return text;
};

/** @param {number} seconds */
const formatTime = (seconds) => {
  const whole = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(whole / 60);
  const remainder = String(whole % 60).padStart(2, '0');
  return `${minutes}:${remainder}`;
};

/** @param {Blob} blob */
const toBase64 = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener('load', () => {
      const result = String(reader.result);
      resolve(result.slice(result.indexOf(',') + 1));
    });
    reader.addEventListener('error', () => reject(reader.error));
    reader.readAsDataURL(blob);
  });

/**
 * Greedy word-wrap against the canvas' current font.
 * @param {CanvasRenderingContext2D} context
 * @param {string} text
 * @param {number} maxWidth
 */
const wrapText = (context, text, maxWidth) => {
  const lines = [];
  for (const paragraph of text.split(/\n+/)) {
    let line = '';
    for (const word of paragraph.split(/\s+/)) {
      if (!word) continue;
      const candidate = line ? `${line} ${word}` : word;
      if (context.measureText(candidate).width > maxWidth && line) {
        lines.push(line);
        line = word;
      } else {
        line = candidate;
      }
    }
    if (line) lines.push(line);
  }
  return lines;
};

if (!customElements.get('plw-story-form')) {
  customElements.define('plw-story-form', PlwStoryForm);
}
